import { StuutApi } from '@stuut/api-sdk';
import { HTTPClient } from '@stuut/api-sdk/lib/http';
import type { Security } from '@stuut/api-sdk/models';

import { authClient } from './api';

const security = async (): Promise<Security> => {
  const token = await authClient.getAuthToken();
  return { httpBearer: token };
};

/**
 * Custom HTTP client that retries on 401 by refreshing the auth token.
 * The Speakeasy SDK doesn't share the Axios interceptor from AuthClient,
 * so 401s from expired tokens need to be handled here.
 */
const httpClient = new HTTPClient({
  fetcher: async (input, init) => {
    const response = await fetch(input, init);

    if (response.status !== 401) {
      return response;
    }

    try {
      await authClient.loginWithRefreshToken();
    } catch {
      return response;
    }

    if (!authClient.hasAuthToken) {
      return response;
    }

    // Clone the original request with the new token
    const newHeaders = new Headers(init?.headers);
    newHeaders.set('Authorization', `Bearer ${authClient.authToken}`);
    return fetch(input, { ...init, headers: newHeaders });
  },
});

export const sdk = new StuutApi({
  retryConfig: { strategy: 'none' },
  httpClient,
  httpBearer: async () => {
    const token = await security();
    return token.httpBearer || '';
  },
  serverURL: process.env.STUUT_API_URL || '',
});
